import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { FormGroup, ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { ToolbarService } from '../../../services/toolbar.service';
import { getErrorMessage } from '../../../consts/form-field-errors';
import { MessagingService } from '../../../components/messaging/messaging.service';
import { EscapeDialogHelper } from '../../../helpers/escape-dialog.helper';
import { GuidanceBoxComponent } from '../../../components/guidance-box/guidance-box.component';
import { switchMap } from 'rxjs/operators';
import { RepairerAdminService } from '../../../services/repairer-admin.service';
import { ModalConfigsHelper } from '../../../helpers/modal-configs.helper';
import { smallModalConfig } from '../../../consts/modal-config.const';
import { CurrentUserService } from '../../../services/currentUser.service';
import { Address } from '../../../model/address.model';
import { QUOTING_SYSTEMS } from '../../../consts/quotingSystems.const';
import { EMAIL_PATTERN_STRING } from '../../../consts/patterns.const';
import { PhoneValidator } from '../../../validators/phone.validator';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DateInputHelper } from '../../../helpers/date-input.helper';
import { RepairerSiteUpdate } from '../../../model/user.model';
import moment from 'moment';
import { EIO_DATE_BE_FORMAT } from '../../../consts/localization.const';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/divider';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { BrowserAutoFillDisabledDirective } from '../../../directives/browser-auto-fill-disabled.directive';
import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { AddressBlockComponent } from '../../../components/address-block/address-block.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { RepairerStatusBarComponent } from './repairer-status-bar.component';
import { DatePipe as DateEpochPipe } from '../../../pipes/date.pipe';
import { TimePipe } from '../../../pipes/time.pipe';

@Component({
  selector: 'app-edit-company-information',
  templateUrl: './edit-company-information.component.html',
  imports: [
    MatDialogTitle,
    MatIconButton,
    MatDialogClose,
    MatIcon,
    MatDivider,
    ReactiveFormsModule,
    MatDialogContent,
    MatFormField,
    MatLabel,
    MatInput,
    BrowserAutoFillDisabledDirective,
    MatError,
    NgTemplateOutlet,
    MatSelect,
    MatOption,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    AddressBlockComponent,
    MatCheckbox,
    MatDialogActions,
    MatButton,
    DatePipe,
    NgClass,
    RepairerStatusBarComponent,
    DateEpochPipe,
    TimePipe
  ]
})
export class EditCompanyInformationComponent implements OnInit {
  editAddressForm: FormGroup;
  quotingSystemList = QUOTING_SYSTEMS;
  pending = signal<boolean>(false);
  isPublished: boolean;
  protected readonly getErrorMessage = getErrorMessage;

  data = inject(MAT_DIALOG_DATA);
  currentUserService = inject(CurrentUserService);
  #dialogRef = inject(MatDialogRef<EditCompanyInformationComponent>);
  #fb = inject(UntypedFormBuilder);
  #toolbarService = inject(ToolbarService);
  #message = inject(MessagingService);
  #escapeDialogRef = inject(EscapeDialogHelper);
  #confirmBox = inject(MatDialog);
  #repAdminService = inject(RepairerAdminService);
  #modalConfigsHelper = inject(ModalConfigsHelper);
  #destroyRef = inject(DestroyRef);
  dateInputHelper = inject(DateInputHelper);

  isSysAdmin = signal<boolean>(this.currentUserService.currentUserPrivileges().includes('SYSADMIN'));

  ngOnInit(): void {
    this.isPublished = ['ACTIVE', 'SUSPENDED', 'UNAVAILABLE'].includes(this.data?.repairer?.status);
    this.data.repairer.physicalAddress.country = this.data.repairer.country;
    this.data.repairer.billingAddress.country = this.data.repairer.country;
    this.populateEditAddressForm();

    this.#escapeDialogRef.escapeDialog(this.#dialogRef);
  }

  populateEditAddressForm() {
    this.editAddressForm = this.#fb.group({
      companyName: [
        {
          value: this.data.repairer.companyName || '',
          disabled: !this.isSysAdmin() || this.isPublished
        },
        Validators.required
      ],
      email: [this.data.repairer.email || '', [Validators.required, Validators.pattern(EMAIL_PATTERN_STRING)]],
      primaryAccountsEmail: [
        this.data.repairer.primaryAccountsEmail || '',
        [Validators.required, Validators.pattern(EMAIL_PATTERN_STRING)]
      ],
      secondaryAccountsEmail: [
        this.data.repairer.secondaryAccountsEmail || '',
        Validators.pattern(EMAIL_PATTERN_STRING)
      ],
      phoneNumber: [this.data.repairer.phoneNumber || '', [Validators.required, PhoneValidator]],
      licenseNumber: [
        {
          value: this.data.repairer.licenseNumber || '',
          disabled: !this.isSysAdmin()
        }
      ],
      physicalAddress: this.data.repairer.physicalAddress,
      billingAddress: [
        {
          value: this.data.repairer.billingAddress,
          disabled: this.data.repairer.sameAsPhysical
        }
      ],
      sameAsPhysical: this.data.repairer.sameAsPhysical,
      repairerGroupId: this.data.repairer.repairerGroupId,
      quotingSystem: [
        {
          value: this.data.repairer.quotingSystem || '',
          disabled: !this.isSysAdmin()
        }
      ],
      licenseRenewalDate: [
        {
          value: this.data.repairer.licenseRenewalDate,
          disabled: !this.isSysAdmin()
        }
      ]
    });

    this.phoneNumber.markAsTouched();

    this.sameAsPhysical.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((value) => {
      this.billingAddress.patchValue(this.physicalAddress.value);
      value ? this.billingAddress.disable() : this.billingAddress.enable();
    });

    this.companyName.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      if (this.companyName.invalid) {
        this.companyName.markAllAsTouched();
      }
    });

    this.physicalAddress.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((address: Address) => this.sameAsPhysical.value && this.billingAddress.patchValue(address));
  }

  updateRepairerSites() {
    this.pending.set(true);
    this.#toolbarService.setCloudState('SAVING');
    this.#repAdminService.updateRepairerSiteWithAddress(this.data.repairer.id, this.#getRepairerSiteForm()).subscribe({
      next: () => {
        this.#dialogRef.close('Success');
        this.#toolbarService.setCloudState('RESTING');
        this.pending.set(false);
      },
      error: () => {
        this.#message.error('Could not update the repairer site.');
        this.#toolbarService.setCloudState('RESTING');
        this.pending.set(false);
      }
    });
  }

  publishRepairerSite() {
    this.pending.set(true);
    this.#toolbarService.setCloudState('SAVING');
    this.#repAdminService
      .updateRepairerSiteWithAddress(this.data.repairer.id, this.#getRepairerSiteForm())
      .pipe(
        switchMap(() => this.#repAdminService.publishRepairerSite()),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe({
        next: () => {
          this.#dialogRef.close('PublishRepairerSuccessful');
        },
        error: () => {
          this.#message.error('Publish failed, please contact Support.');
          this.#toolbarService.setCloudState('RESTING');
          this.pending.set(false);
        },
        complete: () => {
          this.#toolbarService.setCloudState('RESTING');
          this.pending.set(false);
          this.#confirmPublishRepairerAdminLocation();
        }
      });
  }

  #getRepairerSiteForm(): RepairerSiteUpdate {
    const repairerSiteUpdate = this.editAddressForm.value;
    repairerSiteUpdate.licenseRenewalDate = !!this.licenseRenewalDate.value
      ? moment(this.licenseRenewalDate.value).format(EIO_DATE_BE_FORMAT)
      : null;
    return repairerSiteUpdate;
  }

  #confirmPublishRepairerAdminLocation(): void {
    const data = {
      title: 'Location Published',
      alertMessage:
        'Your location has successfully been published and will now be available to all EstImage insurers. This completes your registration.',
      confirmButton: 'OK'
    };
    this.#confirmBox.open(
      GuidanceBoxComponent,
      this.#modalConfigsHelper.buildMediumModalConfig(data, smallModalConfig)
    );
  }

  get companyName() {
    return this.editAddressForm.get('companyName');
  }
  get email() {
    return this.editAddressForm.get('email');
  }
  get primaryAccountsEmail() {
    return this.editAddressForm.get('primaryAccountsEmail');
  }
  get secondaryAccountsEmail() {
    return this.editAddressForm.get('secondaryAccountsEmail');
  }
  get phoneNumber() {
    return this.editAddressForm.get('phoneNumber');
  }
  get licenseNumber() {
    return this.editAddressForm.get('licenseNumber');
  }
  get physicalAddress() {
    return this.editAddressForm.get('physicalAddress');
  }
  get billingAddress() {
    return this.editAddressForm.get('billingAddress');
  }
  get sameAsPhysical() {
    return this.editAddressForm.get('sameAsPhysical');
  }
  get repairerGroupId() {
    return this.editAddressForm.get('repairerGroupId');
  }
  get quotingSystem() {
    return this.editAddressForm.get('quotingSystem');
  }
  get licenseRenewalDate() {
    return this.editAddressForm.get('licenseRenewalDate');
  }
}
